import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ChevronDown, Plus, Search } from 'lucide-react';
import {
  getSubGroupings,
  createSubGrouping,
} from '../db/database';
import type { SubGrouping } from '../db/database';

// ============================================================================
// PROPS
// ============================================================================

interface SubGroupingComboboxProps {
  engagementId: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Optional placeholder override */
  placeholder?: string;
  /**
   * When true, the first option in the dropdown will be a "None" / clear option.
   * Defaults to true.
   */
  showNoneOption?: boolean;
  /** Label for the "None" option. Defaults to "None". */
  noneLabel?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function SubGroupingCombobox({
  engagementId,
  value,
  onChange,
  disabled = false,
  placeholder = 'Search or add SubGrouping…',
  showNoneOption = true,
  noneLabel = 'None',
}: SubGroupingComboboxProps) {
  // ── Internal state ────────────────────────────────────────────────────────
  const [records, setRecords] = useState<SubGrouping[]>([]);
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Load SubGroupings ─────────────────────────────────────────────────────
  const loadRecords = useCallback(async () => {
    if (!engagementId) return;
    try {
      const data = await getSubGroupings(engagementId);
      setRecords(data);
    } catch (err) {
      console.error('SubGroupingCombobox: failed to load records', err);
    }
  }, [engagementId]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  // Sync external value → inputValue when the parent changes it
  // (e.g. auto-clear when grouping changes)
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const names = useMemo(() => records.map((r) => r.name).sort(), [records]);

  const filtered = useMemo(() => {
    if (!inputValue.trim()) return names;
    const q = inputValue.toLowerCase();
    return names.filter((n) => n.toLowerCase().includes(q));
  }, [names, inputValue]);

  const trimmedInput = inputValue.trim();

  /** True when the typed name is new and could be created */
  const isNewName = useMemo(() => {
    if (!trimmedInput) return false;
    return !names.some((n) => n.toLowerCase() === trimmedInput.toLowerCase());
  }, [trimmedInput, names]);

  // ── Click-outside to close ────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const selectValue = (v: string) => {
    setInputValue(v);
    onChange(v);
    setIsOpen(false);
    setError('');
  };

  const handleCreate = async () => {
    if (!trimmedInput || !engagementId || isCreating) return;
    setIsCreating(true);
    setError('');
    try {
      await createSubGrouping(engagementId, trimmedInput);
      await loadRecords();
      selectValue(trimmedInput);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create SubGrouping.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isNewName) {
        handleCreate();
      } else if (filtered.length === 1) {
        // Auto-select the sole match
        selectValue(filtered[0]);
      }
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
            setError('');
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-full pl-9 pr-10 py-2 rounded-lg text-sm
            bg-white dark:bg-slate-800
            text-slate-900 dark:text-slate-100
            placeholder:text-slate-400 dark:placeholder:text-slate-500
            border border-slate-200 dark:border-slate-700
            focus:outline-none focus:ring-2 focus:ring-sage-500 focus:border-sage-500
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors duration-200"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => {
            if (!disabled) {
              setIsOpen(!isOpen);
              inputRef.current?.focus();
            }
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Inline error */}
      {error && (
        <p className="text-xs text-red-500 dark:text-red-400 mt-1 pl-1">{error}</p>
      )}

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 glass-panel rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {/* "None" / clear option */}
          {showNoneOption && (
            <div
              onClick={() => selectValue('')}
              className="px-3 py-2 cursor-pointer text-sm italic
                text-slate-500 dark:text-slate-400
                hover:bg-slate-100 dark:hover:bg-slate-700/60
                transition-colors"
            >
              {noneLabel}
            </div>
          )}

          {/* Existing matches */}
          {filtered.map((name) => (
            <div
              key={name}
              onClick={() => selectValue(name)}
              className={`px-3 py-2 cursor-pointer text-sm transition-colors
                text-slate-900 dark:text-slate-100
                hover:bg-slate-100 dark:hover:bg-slate-700/60
                ${value === name ? 'bg-sage-50 dark:bg-sage-900/20 font-medium' : ''}`}
            >
              {name}
            </div>
          ))}

          {/* No results (only when not showing the add-new prompt) */}
          {filtered.length === 0 && !isNewName && (
            <div className="px-3 py-2 text-sm italic text-slate-500 dark:text-slate-400">
              No SubGroupings found
            </div>
          )}

          {/* "+ Add '[Name]'" option */}
          {isNewName && (
            <div
              onClick={handleCreate}
              className="px-3 py-2 cursor-pointer text-sm font-medium flex items-center gap-2
                text-sage-600 dark:text-sage-400
                bg-sage-50/60 dark:bg-sage-900/20
                border-t border-sage-200 dark:border-sage-800
                hover:bg-sage-100 dark:hover:bg-sage-900/30
                transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>
                Add &lsquo;{trimmedInput}&rsquo;
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
